import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Send, Bot, User, Zap, Target, TrendingUp,
  Calendar, Sparkles, RefreshCw, Crown, ChevronRight
} from "lucide-react";

import { getApiBaseUrl } from "@/services/api";
const API_URL = getApiBaseUrl();

/**
 * Banking AI Coach - CLARIAS Banking Execution AI Interface
 * Implements chat interface for all 25 sections of the AI system
 */
const BankingAICoachPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [stats, setStats] = useState(null);
  const messagesEndRef = useRef(null);

  const isPro = user?.role === "PRO" || user?.banking_role === "PRO";

  // Quick action prompts
  const quickActions = [
    { label: "Today's Plan", prompt: "Generate my daily execution plan for today", icon: Calendar },
    { label: "CA Sprint", prompt: "Give me today's current affairs sprint with recall questions", icon: Zap },
    { label: "My Analytics", prompt: "Show my performance analytics and section balance", icon: TrendingUp },
    { label: "Weak Areas", prompt: "Analyze my weak areas and give me improvement strategy", icon: Target },
  ];

  useEffect(() => {
    fetchStats();
    // Add welcome message
    setMessages([{
      role: "assistant",
      content: `Welcome to **CLARIAS Banking Execution AI**! 🏦

I'm your Performance Operating System for Banking exam preparation.

I can help you with:
• **Daily Execution Plan** - Personalized practice schedule
• **CA Sprint** - Current affairs in retention format
• **Banking Capsules** - Micro-learning concepts
• **Performance Analysis** - Beat-yesterday tracking
• **Mock Analysis** - Detailed test breakdowns

What would you like to work on today?`,
      timestamp: new Date()
    }]);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/banking/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  const sendMessage = async (messageText = input) => {
    if (!messageText.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/banking/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          message: messageText,
          session_id: sessionId
        })
      });

      const data = await res.json();

      if (data.session_id) {
        setSessionId(data.session_id);
      }

      const assistantMessage = {
        role: "assistant",
        content: data.response || "Sorry, I couldn't process that request.",
        timestamp: new Date(),
        is_pro: data.is_pro,
        limit_reached: data.limit_reached
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (err) {
      console.error("Chat error:", err);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "I'm having trouble connecting. Please try again.",
        timestamp: new Date(),
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatMessage = (content) => {
    // Simple markdown-like formatting
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code class="bg-slate-100 px-1 rounded">$1</code>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/banking')}
                className="bg-white/10 hover:bg-white/20 text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Bot className="w-5 h-5" />
                </div>
                <div>
                  <h1 className="font-bold flex items-center gap-2">
                    CLARIAS AI Coach
                    {isPro && <Crown className="w-4 h-4 text-amber-300" />}
                  </h1>
                  <p className="text-xs text-emerald-200">Banking Execution Intelligence</p>
                </div>
              </div>
            </div>
            
            {stats && (
              <div className="hidden md:flex items-center gap-4 text-sm">
                <div className="text-center">
                  <p className="font-bold">{stats.streak?.current || 0}</p>
                  <p className="text-xs text-emerald-200">Streak</p>
                </div>
                <div className="text-center">
                  <p className="font-bold">{stats.total_questions || 0}</p>
                  <p className="text-xs text-emerald-200">MCQs</p>
                </div>
                <div className="text-center">
                  <p className="font-bold">{stats.overall_accuracy || 0}%</p>
                  <p className="text-xs text-emerald-200">Accuracy</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {quickActions.map((action, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                onClick={() => sendMessage(action.prompt)}
                disabled={loading}
                className="whitespace-nowrap flex-shrink-0"
              >
                <action.icon className="w-4 h-4 mr-1" />
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              {msg.role === "assistant" && (
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-emerald-600" />
                </div>
              )}
              
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.role === "user"
                    ? "bg-emerald-600 text-white rounded-br-md"
                    : "bg-white border shadow-sm rounded-bl-md"
                }`}
              >
                <div 
                  className={`text-sm ${msg.role === "user" ? "text-white" : "text-slate-700"}`}
                  dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                />
                {msg.limit_reached && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <Button
                      size="sm"
                      onClick={() => navigate('/account-settings')}
                      className="bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                    >
                      <Crown className="w-4 h-4 mr-1" />
                      Upgrade to PRO
                    </Button>
                  </div>
                )}
              </div>
              
              {msg.role === "user" && (
                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          ))}
          
          {loading && (
            <div className="flex gap-3 justify-start">
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                <Bot className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="bg-white border shadow-sm rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-2 text-slate-500">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Analyzing...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white border-t">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex gap-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about your preparation, request daily plan, or get banking capsules..."
              className="flex-1"
              disabled={loading}
            />
            <Button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          
          {!isPro && (
            <p className="text-xs text-slate-500 mt-2 text-center">
              Free tier: 10 AI sessions/day • <span className="text-emerald-600 cursor-pointer" onClick={() => navigate('/account-settings')}>Upgrade for unlimited</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BankingAICoachPage;
